/**
 * Handle edit mode for line items in cart
 * When editing a line item, remove the old one before adding the new variant
 */

(function() {
  let isProcessingEdit = false;

  // Change button text when modal content loads
  const updateButtonTextInModal = (modal) => {
    const updateText = modal.getAttribute('data-update-text') || 
                       window.themeVariables?.strings?.updateDetails || 
                       'Update Details';
    
    console.log('Updating button text to:', updateText);
    
    const buyButtons = modal.querySelector('buy-buttons');
    if (!buyButtons) {
      console.log('Buy buttons not found');
      return false;
    }
    
    const submitButton = buyButtons.querySelector('button[type="submit"]:not(.shopify-payment-button__button)');
    if (!submitButton) {
      console.log('Submit button not found');
      return false;
    }
    
    const buttonContent = submitButton.querySelector('button-content');
    if (!buttonContent) {
      console.log('Button content not found');
      return false;
    }
    
    console.log('Current button text:', buttonContent.textContent);
    
    // Clear and set new text
    buttonContent.textContent = updateText;
    
    // Update animation attributes if they exist
    if (buttonContent.hasAttribute('up-text')) {
      buttonContent.setAttribute('up-text', updateText);
    }
    if (buttonContent.hasAttribute('rotated-text')) {
      buttonContent.setAttribute('rotated-text', updateText);
    }
    
    // Also update aria-label if exists
    if (submitButton.hasAttribute('aria-label')) {
      submitButton.setAttribute('aria-label', updateText);
    }
    
    console.log('Button text updated to:', buttonContent.textContent);
    return true;
  };

  // Watch for modal content changes
  document.addEventListener('DOMContentLoaded', function() {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) {
            // Check if it's a quick-buy-modal in edit mode
            const modal = node.matches?.('quick-buy-modal[data-mode="edit"]') ? 
                         node : 
                         node.querySelector?.('quick-buy-modal[data-mode="edit"]');
            
            if (modal) {
              const buyButtons = modal.querySelector('buy-buttons');
              if (buyButtons) {
                console.log('Edit modal content detected');
                
                // Update immediately
                updateButtonTextInModal(modal);
                
                // Continuously monitor and update button text
                const modalObserver = new MutationObserver(() => {
                  const buttonContent = modal.querySelector('buy-buttons button[type="submit"]:not(.shopify-payment-button__button) button-content');
                  if (buttonContent) {
                    const updateText = modal.getAttribute('data-update-text') || 
                                      window.themeVariables?.strings?.updateDetails || 
                                      'Update Details';
                    
                    // Only update if text has changed back to original
                    if (buttonContent.textContent.trim() !== updateText) {
                      console.log('Button text changed from:', buttonContent.textContent, 'to:', updateText);
                      buttonContent.textContent = updateText;
                      
                      // Update animation attributes
                      if (buttonContent.hasAttribute('up-text')) {
                        buttonContent.setAttribute('up-text', updateText);
                      }
                      if (buttonContent.hasAttribute('rotated-text')) {
                        buttonContent.setAttribute('rotated-text', updateText);
                      }
                    }
                  }
                });
                
                // Observe all changes in the modal
                modalObserver.observe(modal, {
                  childList: true,
                  subtree: true,
                  characterData: true,
                  characterDataOldValue: true
                });
                
                // Keep observer active while modal is open
                const checkModalOpen = setInterval(() => {
                  if (!modal.hasAttribute('open') && !modal.open) {
                    modalObserver.disconnect();
                    clearInterval(checkModalOpen);
                    console.log('Modal closed, observer disconnected');
                  }
                }, 500);
              }
            }
          }
        });
      });
    });
    
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  });

  // Intercept form submission in edit mode
  document.addEventListener('submit', async function(event) {
    const form = event.target;
    
    // Only handle cart/add forms
    if (!form.action.includes('/cart/add')) {
      return;
    }
    
    // Check if the form is inside a quick-buy-modal with edit mode
    const quickBuyModal = form.closest('quick-buy-modal');
    
    if (!quickBuyModal) {
      return;
    }
    
    const mode = quickBuyModal.getAttribute('data-mode');
    const lineKey = quickBuyModal.getAttribute('data-line-key');
    
    // Only proceed if this is an edit operation
    if (mode !== 'edit' || !lineKey || isProcessingEdit) {
      return;
    }
    
    // Prevent default form submission
    event.preventDefault();
    event.stopImmediatePropagation();
    
    isProcessingEdit = true;
    
    try {
      // First, remove the old line item
      const removeResponse = await fetch(`${Shopify.routes.root}cart/change.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          id: lineKey,
          quantity: 0
        })
      });
      
      if (!removeResponse.ok) {
        throw new Error('Failed to remove old item');
      }
      
      // Then, submit the form to add the new variant
      const formData = new FormData(form);
      const submitButtons = Array.from(form.elements).filter(button => button.type === 'submit');
      
      // Set loading state
      submitButtons.forEach(button => button.setAttribute('aria-busy', 'true'));
      
      let sectionsToBundle = [];
      document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { 
        bubbles: true, 
        detail: { sections: sectionsToBundle } 
      }));
      
      formData.set('sections', sectionsToBundle.join(','));
      formData.set('sections_url', `${Shopify.routes.root}variants/${form.id.value}`);
      
      const addResponse = await fetch(`${Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      });
      
      submitButtons.forEach(button => button.removeAttribute('aria-busy'));
      
      const responseJson = await addResponse.json();
      
      if (addResponse.ok) {
        // Get updated cart
        const cartContent = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
        cartContent['sections'] = responseJson['sections'];
        
        const items = responseJson.hasOwnProperty('items') ? responseJson['items'] : [responseJson];
        
        // Dispatch success events
        form.dispatchEvent(new CustomEvent('variant:add', {
          bubbles: true,
          detail: { items, cart: cartContent }
        }));
        
        document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
          bubbles: true,
          detail: {
            baseEvent: 'variant:add',
            cart: cartContent
          }
        }));
        
        // Close the modal first
        if (quickBuyModal.hide) {
          quickBuyModal.hide();
        }
        
        // For cart page, reload the page to show updated cart
        if (window.themeVariables && window.themeVariables.settings.pageType === 'cart') {
          window.location.reload();
        } else {
          // For other pages (with cart drawer), force refresh
          setTimeout(() => {
            document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { 
              bubbles: true 
            }));
          }, 100);
          
          // Show success message
          if (window.themeVariables && window.themeVariables.settings.cartType === 'message') {
            document.dispatchEvent(new CustomEvent('toast:show', {
              detail: {
                message: (window.themeVariables.strings.addedToCart || 'Item updated').replace('{{product_title}}', items?.[0]?.title)
              }
            }));
          }
        }
      } else {
        // Handle error
        form.dispatchEvent(new CustomEvent('cart:error', {
          bubbles: true,
          detail: { error: responseJson['message'] }
        }));
        
        document.dispatchEvent(new CustomEvent('toast:show', {
          detail: {
            message: responseJson['message'],
            tone: 'error'
          }
        }));
        
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      }
    } catch (error) {
      console.error('Error during cart line item edit:', error);
      
      // Show error message
      document.dispatchEvent(new CustomEvent('toast:show', {
        detail: {
          message: 'Failed to update cart item',
          tone: 'error'
        }
      }));
      
      // Refresh cart to ensure consistency
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    } finally {
      isProcessingEdit = false;
    }
  }, true); // Use capture phase to intercept before other handlers
})();

